import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useClub } from '../../app/club';
import { useRepo } from '../../app/services';
import { useClubMutation, useGroups, usePersons } from '../../data/hooks';
import { Badge, Button, EmptyState, ErrorState, Loading, PageHeader, Panel, type Tone } from '../../components/ui';
import { ExpiryChoiceField } from '../../components/ExpiryChoiceField';
import {
  autoMapColumns,
  buildPreview,
  mappingErrors,
  parseCsv,
  TARGET_FIELDS,
  type ColumnMapping,
  type ParsedCsv,
  type PreviewStatus,
  type TargetField,
} from '../../lib/csv';
import { resolveExpiry, UNSET_EXPIRY, type ExpiryChoice } from '../../lib/expiry';
import type { ImportResult } from '../../data/repository';
import { errorText } from '../../lib/errors';

const STATUS: Record<PreviewStatus, { label: string; tone: Tone }> = {
  new: { label: 'Nieuw', tone: 'green' },
  update: { label: 'Bijwerken', tone: 'ink' },
  duplicate_in_file: { label: 'Dubbel in bestand', tone: 'yellow' },
  error: { label: 'Fout', tone: 'red' },
};
const STEPS = ['Bestand', 'Kolommen koppelen', 'Voorbeeld en groepen'];
const MAX_BYTES = 5 * 1024 * 1024;

export function ImportPage() {
  const { readOnly } = useClub();
  if (readOnly) {
    return (
      <EmptyState title="Importeren kan niet in alleen-lezen">
        Je kijkt mee zonder te wijzigen. Een Antreja-beheerder kan wijzigen inschakelen via de balk bovenaan.
      </EmptyState>
    );
  }
  return <ImportPageInner />;
}

function ImportPageInner() {
  const { orgId, org } = useClub();
  const repo = useRepo();
  const persons = usePersons(orgId);
  const groups = useGroups(orgId);
  const [step, setStep] = useState(0);
  const [fileName, setFileName] = useState('');
  const [parsed, setParsed] = useState<ParsedCsv | null>(null);
  const [mapping, setMapping] = useState<ColumnMapping>({});
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [expiry, setExpiry] = useState<ExpiryChoice>(UNSET_EXPIRY);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const run = useClubMutation(orgId, (input: Parameters<typeof repo.importPersons>[1]) => repo.importPersons(orgId, input));

  const preview = useMemo(
    () => (parsed && persons.data && step === 2 ? buildPreview(parsed, mapping, persons.data) : null),
    [parsed, mapping, persons.data, step],
  );

  if (persons.isLoading || groups.isLoading) return <Loading />;
  if (persons.error || groups.error) return <ErrorState error={persons.error ?? groups.error} />;

  const onFile = async (file: File | undefined) => {
    setError(null);
    if (!file) return;
    if (!/\.(csv|txt)$/i.test(file.name)) {
      setError('Upload een CSV-bestand. Exporteer in Sportlink of Excel als "CSV (puntkomma-gescheiden)".');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError('Het bestand is groter dan 5 MB. Splits het op in kleinere bestanden.');
      return;
    }
    const text = await file.text();
    const p = parseCsv(text);
    if (p.headers.length < 2 || p.rows.length === 0) {
      setError('Dit bestand heeft geen kopregel of geen gegevens.');
      return;
    }
    setFileName(file.name);
    setParsed(p);
    setMapping(autoMapColumns(p.headers));
    setStep(1);
  };

  const mapErrs = mappingErrors(mapping);
  const selectedGroup = groups.data!.filter((g) => groupIds.includes(g.id));
  const earliestGroupEnd = selectedGroup
    .map((g) => g.valid_until)
    .filter(Boolean)
    .sort()[0] as string | undefined;

  const doImport = async () => {
    if (!preview) return;
    let validUntil: string | null = null;
    if (groupIds.length) {
      const r = resolveExpiry(expiry, { groupValidUntil: earliestGroupEnd });
      if (!r.ok) {
        setError(r.error);
        return;
      }
      validUntil = r.validUntil;
    }
    setError(null);
    const rows = preview.rows.filter((r) => r.status === 'new' || r.status === 'update').map((r) => r.person);
    try {
      setResult(await run.mutateAsync({ rows, group_ids: groupIds, valid_until: validUntil }));
    } catch (e) {
      setError(errorText(e, 'Importeren mislukt'));
    }
  };

  const reset = () => {
    setStep(0);
    setParsed(null);
    setMapping({});
    setGroupIds([]);
    setExpiry(UNSET_EXPIRY);
    setResult(null);
    setError(null);
  };

  if (result) {
    return (
      <div className="stack-lg">
        <PageHeader title="Import klaar" />
        <div className="alert alert-ok" role="status">
          {result.created} nieuwe personen, {result.updated} bijgewerkt
          {result.memberships ? `, ${result.memberships} keer aan een groep toegevoegd` : ''}. De toegang gaat bij de volgende sync naar de sloten.
        </div>
        <div className="row">
          <Link className="btn btn-primary" to={`/c/${orgId}/personen`}>
            Naar personen
          </Link>
          <Button onClick={reset}>Nog een bestand importeren</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="stack-lg">
      <PageHeader
        title="Import"
        intro={`Leden van ${org.name} inlezen uit een Sportlink-export of eigen CSV. Bestaande personen worden herkend aan hun relatienummer en bijgewerkt.`}
      />
      <ol className="steps" aria-label="Stappen">
        {STEPS.map((s, i) => (
          <li key={s} aria-current={i === step ? 'step' : undefined}>
            {i + 1}. {s}
          </li>
        ))}
      </ol>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {step === 0 && (
        <Panel className="panel-pad">
          <div
            className="drop"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              void onFile(e.dataTransfer.files[0]);
            }}
          >
            <p style={{ marginBottom: 12 }}>Sleep een CSV-bestand hierheen, of</p>
            <label className="btn btn-primary">
              Kies bestand
              <input type="file" accept=".csv,.txt,text/csv" className="sr-only" onChange={(e) => void onFile(e.target.files?.[0])} />
            </label>
            <p className="muted small" style={{ marginTop: 16 }}>
              Sportlink: Leden › Exporteren › kies minimaal Relatienummer, naam, e-mail en mobiel. Puntkomma of komma als scheidingsteken werkt allebei.
            </p>
          </div>
        </Panel>
      )}

      {step === 1 && parsed && (
        <Panel title="Kolommen koppelen" description={`${fileName}: ${parsed.rows.length} rijen. We hebben de kolommen alvast herkend; controleer ze.`}>
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th scope="col">Antreja-veld</th>
                  <th scope="col">Kolom in je bestand</th>
                  <th scope="col">Voorbeeld</th>
                </tr>
              </thead>
              <tbody>
                {TARGET_FIELDS.map((f) => {
                  const idx = mapping[f.key];
                  const id = `map-${f.key}`;
                  return (
                    <tr key={f.key}>
                      <td>
                        <label htmlFor={id} className="cell-title">
                          {f.label}
                        </label>
                        {f.hint && <span className="cell-sub">{f.hint}</span>}
                      </td>
                      <td>
                        <select
                          id={id}
                          className="input"
                          value={idx ?? ''}
                          onChange={(e) =>
                            setMapping((m) => {
                              const next = { ...m };
                              if (e.target.value === '') delete next[f.key as TargetField];
                              else next[f.key as TargetField] = Number(e.target.value);
                              return next;
                            })
                          }
                        >
                          <option value="">Niet gebruiken</option>
                          {parsed.headers.map((h, i) => (
                            <option key={i} value={i}>
                              {h || `Kolom ${i + 1}`}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="muted small">{idx !== undefined ? parsed.rows.slice(0, 2).map((r) => r[idx]).filter(Boolean).join(', ') : ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {mapErrs.length > 0 && (
            <div className="alert alert-warn" style={{ margin: 20 }}>
              {mapErrs.map((m) => (
                <p key={m}>{m}</p>
              ))}
            </div>
          )}
          <div className="row" style={{ padding: 20 }}>
            <Button onClick={reset}>Ander bestand</Button>
            <Button variant="primary" disabled={mapErrs.length > 0} onClick={() => setStep(2)}>
              Toon voorbeeld
            </Button>
          </div>
        </Panel>
      )}

      {step === 2 && preview && (
        <>
          <Panel title="Voorbeeld" description="Alleen rijen met Nieuw of Bijwerken worden ingelezen.">
            <div className="row" style={{ padding: '14px 20px' }}>
              <Badge tone="green">{preview.counts.new} nieuw</Badge>
              <Badge tone="ink">{preview.counts.update} bijwerken</Badge>
              {preview.counts.duplicate_in_file > 0 && <Badge tone="yellow">{preview.counts.duplicate_in_file} dubbel in bestand</Badge>}
              {preview.counts.error > 0 && <Badge tone="red">{preview.counts.error} met fouten</Badge>}
            </div>
            <div className="table-wrap" style={{ maxHeight: 420, overflowY: 'auto' }}>
              <table className="data stack-mobile">
                <thead>
                  <tr>
                    <th scope="col">Rij</th>
                    <th scope="col">Naam</th>
                    <th scope="col">Relatienummer</th>
                    <th scope="col">E-mail</th>
                    <th scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.slice(0, 300).map((r) => (
                    <tr key={r.person.rowNumber}>
                      <td className="num" data-label="Rij">
                        {r.person.rowNumber + 1}
                      </td>
                      <td className="cell-main">
                        {r.person.full_name || <span className="muted">(leeg)</span>}
                        {r.person.is_minor && <span className="cell-sub">minderjarig</span>}
                      </td>
                      <td data-label="Relatienummer">{r.person.external_ref ?? '—'}</td>
                      <td data-label="E-mail">{r.person.email ?? '—'}</td>
                      <td>
                        <Badge tone={STATUS[r.status].tone}>{STATUS[r.status].label}</Badge>
                        {r.errors.length > 0 && <span className="cell-sub">{r.errors.join('; ')}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

          <Panel title="Toevoegen aan groep (optioneel)" description="Iedereen uit dit bestand krijgt dan de toegang van die groep.">
            <div className="panel-pad stack">
              <fieldset className="field">
                <legend className="sr-only">Groepen</legend>
                {groups.data!.map((g) => (
                  <label key={g.id} className="row" style={{ gap: 8 }}>
                    <input
                      type="checkbox"
                      style={{ width: 18, height: 18 }}
                      checked={groupIds.includes(g.id)}
                      onChange={(e) => setGroupIds((ids) => (e.target.checked ? [...ids, g.id] : ids.filter((x) => x !== g.id)))}
                    />
                    {g.name}
                  </label>
                ))}
              </fieldset>
              {groupIds.length > 0 && (
                <ExpiryChoiceField legend="Einddatum lidmaatschap" value={expiry} onChange={setExpiry} groupValidUntil={earliestGroupEnd} />
              )}
            </div>
          </Panel>

          <div className="row">
            <Button onClick={() => setStep(1)}>Terug naar kolommen</Button>
            <Button variant="primary" disabled={run.isPending || preview.counts.new + preview.counts.update === 0} onClick={() => void doImport()}>
              {preview.counts.new + preview.counts.update} personen importeren
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
